import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AnalyticsFilters } from '@brand-lolly/app/social-media-influencer/modules/social-listening/models/social-listening-analytics.model';
import { advancedPostToQuery } from '@brand-lolly/app/social-media-influencer/modules/social-listening/components/advanced-filters/advanced-filters.types';
import { SlMatchTypeFilter } from '@brand-lolly/app/social-media-influencer/modules/social-listening/components/filters/match-type/match-type.component';
import { recoverUnlessStrict } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/recover-unless-strict';
import { smiUrl } from 'components';
import { map, Observable } from 'rxjs';

export interface TopSiteByVisits {
	name: string;
	avatarUrl: string | null;
	mentions: number;
	mentionsNegative: number;
	mentionsNeutral: number;
	mentionsPositive: number;
	visits: number;
	visitsStatus: 'ok' | 'pending' | 'unavailable';
}

interface TopSiteApi {
	name: string;
	avatar_url: string;
	mentions: number;
	mentions_negative: number;
	mentions_neutral: number;
	mentions_positive: number;
	visits: number;
	visits_status?: 'ok' | 'pending' | 'unavailable';
}

@Injectable({
	providedIn: 'root'
})
export class GetSocialListeningMyBrandAnalyticsTopSitesByVisitsService {
	constructor(private http: HttpClient) {}

	fetch(
		brandId: string,
		cutoff?: Date | null,
		filters?: AnalyticsFilters,
		to?: Date | null,
		matchType?: SlMatchTypeFilter,
		strict = false
	): Observable<TopSiteByVisits[]> {
		const urlBuilder = smiUrl()
			.add('social-listening')
			.add('brand')
			.add(brandId)
			.add('analytics-top-sites-by-visits')
			.query('from', cutoff?.toISOString())
			.query('to', to?.toISOString());
		this.applyFilters(urlBuilder, filters, matchType);
		const url = urlBuilder.done();

		return this.http.get<{ items: TopSiteApi[] }>(url).pipe(
			recoverUnlessStrict(strict, () => ({ items: [] as TopSiteApi[] })),
			map(r => (r.items ?? []).map(i => this.mapApi(i)))
		);
	}

	private mapApi(i: TopSiteApi): TopSiteByVisits {
		return {
			name: i.name,
			avatarUrl: i.avatar_url || null,
			mentions: i.mentions,
			mentionsNegative: i.mentions_negative,
			mentionsNeutral: i.mentions_neutral,
			mentionsPositive: i.mentions_positive,
			visits: i.visits,
			visitsStatus:
				(i.visits_status as 'ok' | 'pending' | 'unavailable') ??
				'pending'
		};
	}

	private applyFilters(
		b: any,
		filters?: AnalyticsFilters,
		matchType?: SlMatchTypeFilter
	): void {
		if (filters?.platforms?.length)
			b.query('platforms', filters.platforms.join(','));
		if (filters?.sentiments?.length)
			b.query('sentiments', filters.sentiments.join(','));
		if (filters?.emotions?.length)
			b.query('emotions', filters.emotions.join(','));
		if (filters?.languages?.length)
			b.query('languages', filters.languages.join(','));
		if (filters?.engagementRate) {
			b.query('engagement_rate_min', String(filters.engagementRate[0]));
			b.query('engagement_rate_max', String(filters.engagementRate[1]));
		}
		if (matchType) b.query('match_type', matchType);
		for (const [advKey, advVal] of Object.entries(
			advancedPostToQuery(filters?.advanced)
		)) {
			b.query(advKey, advVal);
		}
	}
}
